import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  CreateDataSourceRequest,
  DataSource,
  DataSourceCredentials,
  DataSourceType,
  FeatureFlag,
} from "@coeff/api";
import { AssetsHost, COLORS, DataSourceTypeDisplayNames, DB_DEFAULT_PORTS } from "@coeff/utils";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { Layout, Modal, Tooltip } from "antd";
import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";

import {
  Button,
  CardHeader,
  Checkbox,
  ConfirmBox,
  DisabledOverlay,
  DotsLoader,
  ErrorMessage,
  FlexColumn,
  FlexRow,
  Graphics,
  Icons,
  IPWhiteListInfoCard,
  IPWhitelistInfoPopup,
  KeyValueListInput,
  Logos,
  Modal as CoeffModal,
  Input,
  SecurityInfo,
  Typography,
} from "../../../common";
import { useApiClient, useEventManagerContext, useMessage } from "../../../globals/contexts";
import {
  QueryCacheKey,
  useAuthContext,
  WithPostDataSourceCreationProps,
} from "../../context/AuthContext";
import { getMaskedConnectionString, isWarehouseConnectionError } from "../../utils";

import { LimitSchemasInput } from "./LimitSchemasInput";
import {
  SnowflakeKeyPair,
  SnowflakeKeyPairAction,
  SnowflakeKeyPairModel,
} from "./SnowflakeKeypairModel";
import {
  extractTunnelConfig,
  isTunnelEnabled,
  SSHTunnelForm,
  SSHTunnelFormError,
  TunnelConfig,
  validateSSHConfig,
} from "./SSHTunnelForm";

const { ChevronDown, ConnectPlugIcon, TiltedInfoCircle } = Icons;
const {
  Aurora,
  CloudSql,
  Neon,
  Supabase,
  Clickhouse,
  Azure,
  Timescaledb,
  Databricks,
  MongoDB,
  Motherduck,
  MSSQL,
  MySQL,
  Postgres,
  Redshift,
  Snowflake,
} = Logos;

const { Content, Footer } = Layout;

const StyledContent = styled(Content)<{ $hidePageHeader: boolean }>`
  && {
    height: 100%;
    min-height: calc(100vh - 128px);
    margin-top: ${props => (props.$hidePageHeader ? "0" : "64px")};
    background-color: ${COLORS.white};
    padding: 14px 0px;

    .datasourceLogo {
      margin: auto;
      display: block;
    }

    .graphicsLogo {
      .datasourceLogo {
        width: 60px;
        height: 60px;
      }
    }
  }
`;

const HelpButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.coeblue4};
  outline: none;
  margin: auto;
  display: block;
  font-weight: 500;
  cursor: pointer;
`;

const FormField = styled.div`
  display: block;
  width: auto;
  margin: 15px;
  color: ${COLORS.black85};

  .coeff__input {
    height: 32px;
    margin: auto;
    border-color: ${COLORS.black15};
  }

  .coeff__select,
  .coeff__input {
    margin-top: 2px;
  }
`;

const StyledInstructionText = styled.div`
  color: ${COLORS.black45};
  font-size: 0.86em;
  font-weight: 600;
  padding: 10px;
  line-height: 1.4em;

  &.simple-text {
    font-weight: 400;
    padding: 6px 0 0 0;

    .highlighted-text {
      color: ${COLORS.coeblue4};
      display: inline;

      &:hover {
        cursor: pointer;
      }
    }
  }
`;

const ButtonSection = styled.section`
  width: 100%;
  display: flex;
  padding: 20px 5px 10px;
  background: white;
  justify-content: center;
  box-shadow: 0px -10px 10px -10px #dddddd;

  .ant-btn {
    flex: 1;
    margin: 0px 10px;
    max-width: 200px;

    svg {
      width: 1rem;
      margin-right: 5px;
      position: relative;
      top: 2px;
    }
  }

  &.ip-info-footer {
    box-shadow: none;
  }
`;

const ErrorText = styled.div`
  color: ${COLORS.red};
`;

const StyledFooter = styled(Footer)`
  position: sticky;
  bottom: 0px;
  padding: 0px;
  background: white;
  z-index: 250;
`;

const StyledCardHeader = styled(CardHeader)`
  margin: 15px;
  font-size: 18px;
  font-weight: 400;
`;

const StyledButton = styled(Button)`
  &.ant-btn {
    height: 22px;
    border: none;
    background: none;
  }
`;

const StyledCoeffModal = styled(CoeffModal)`
  .ant-modal-footer {
    border: none;
  }
`;

const ModalImage = styled.img<{ minHeight?: number }>`
  width: 100%;
  max-width: 450px;
  height: auto;
  margin: 16px 0px;
  border-radius: 8px;
  display: block;
  object-fit: contain;
  min-height: ${({ minHeight }) => (minHeight != null ? `${minHeight}px` : "auto")};
`;

const LOGOS_BY_DATA_SOURCE_TYPE: Partial<Record<DataSourceType, React.ReactNode>> = {
  mysql: <MySQL className="datasourceLogo" />,
  redshift: <Redshift className="datasourceLogo" />,
  postgres: <Postgres className="datasourceLogo" />,
  aurora_postgres: <Aurora className="datasourceLogo" />,
  aurora_mysql: <Aurora className="datasourceLogo" />,
  cloud_sql_postgres: <CloudSql className="datasourceLogo" />,
  cloud_sql_mysql: <CloudSql className="datasourceLogo" />,
  supabase: <Supabase className="datasourceLogo" />,
  neon: <Neon className="datasourceLogo" />,
  timescaledb: <Timescaledb className="datasourceLogo" />,
  azure_postgres: <Azure className="datasourceLogo" />,
  azure_mysql: <Azure className="datasourceLogo" />,
  azure_sql: <Azure className="datasourceLogo" />,
  clickhouse: <Clickhouse className="datasourceLogo" />,
  snowflake: <Snowflake className="datasourceLogo" />,
  mssql: <MSSQL className="datasourceLogo" />,
  mongodb: <MongoDB className="datasourceLogo" />,
  databricks: <Databricks className="datasourceLogo" />,
  motherduck: <Motherduck className="datasourceLogo" />,
};

type Props = WithPostDataSourceCreationProps & {
  disableMessages?: boolean;
  dataSourceType: DataSourceType;
  customDescription?: ReactNode;
  isPPressComponent?: boolean;
  useSecondaryForm?: boolean;
  dataSource?: DataSource;
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  onDataSourceUpserted: (dataSource: DataSource) => void;
  onCancel: () => void;
  // Host-supplied connection summary rendered in edit mode (e.g. the addon's
  // DataSourceItem). The content owns the schema state and hands the host
  // `setAvailableSchemas` so it can populate the schema picker.
  renderDataSourceItem?: (args: {
    dataSource?: DataSource;
    setAvailableSchemas: (schemas: string[]) => void;
  }) => React.ReactNode;
  // Host-supplied "Need help connecting?" card (addon's ConnectionHelpCard depends
  // on addon-only FAQ links + support chat, so the host injects it).
  renderConnectionHelpCard?: (args: { dataSourceType: string }) => React.ReactNode;
};

export const AuthorizeSQLContentBase: React.FC<Props> = ({
  disableMessages,
  dataSourceType,
  isPPressComponent,
  customDescription = "",
  useSecondaryForm,
  dataSource: initialDataSource,
  fromConnectedSource,
  fromOnboarding,
  initPostDataSourceCreation,
  onDataSourceUpserted,
  onCancel,
  renderDataSourceItem,
  renderConnectionHelpCard,
}) => {
  const message = useMessage();

  const { value: enableSnowflakeKeyPairAuth } = useFeatureGate(
    FeatureFlag.EnableSnowflakeKeyPairAuth
  );

  const apiClient = useApiClient();

  const { track } = useEventManagerContext();

  const { sentryCapture, getDataSourceApiErrorMessage } = useAuthContext();

  const { data: dataSource } = useQuery(
    [QueryCacheKey.DATA_SOURCE, initialDataSource?.data_source_id],
    () =>
      apiClient
        .getDataSource({
          data_source_id: initialDataSource?.data_source_id!,
        })
        .then(response => response.data.data_source),
    {
      enabled: Boolean(initialDataSource?.data_source_id),
      initialData: initialDataSource,
      onError: error => {
        console.error("Error fetching data source", error);
        sentryCapture?.({ error });
      },
    }
  );

  const [host, setHost] = useState(dataSource?.credentials?.host || "");

  const [dbName, setDbName] = useState(dataSource?.credentials?.database_name || "");

  const [username, setUsername] = useState(dataSource?.credentials?.username || "");

  const [password, setPassword] = useState(dataSource?.credentials?.password || "");

  const [port, setPort] = useState<number | undefined>(
    dataSource?.credentials?.port ?? DB_DEFAULT_PORTS[dataSourceType]
  );

  const [warehouse, setWarehouse] = useState(
    dataSource?.credentials?.snowflake_warehouse_name ?? ""
  );

  const [useTls, setUseTls] = useState(!dataSource?.credentials?.disable_tls);

  const [customCallHeaders, setCustomCallHeaders] = useState<Array<{ key: string; value: string }>>(
    dataSource?.credentials?.arrow_flight_sql_call_headers?.length
      ? dataSource.credentials.arrow_flight_sql_call_headers
      : [{ key: "", value: "" }]
  );

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(
    Boolean(dataSource?.credentials?.selected_schemas?.length)
  );

  const [loading, setLoading] = useState(false);

  const [reloadWarning, setReloadWarning] = useState(false);

  const [showWhitelistInfoPopup, setShowWhitelistInfoPopup] = useState(false);

  const [showWhiteListInfoFooter, setShowWhiteListInfoFooter] = useState(false);

  const [ipInfoFooterShown, setIpInfoFooterShown] = useState(false);

  const [schemaLoading, setSchemaLoading] = useState(false);

  const [showHelpModal, setShowHelpModal] = useState(false);

  const [showAccountIdentifierModal, setShowAccountIdentifierModal] = useState(false);

  const [showDatabaseNameModal, setShowDatabaseNameModal] = useState(false);

  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [sourceError, setSourceError] = useState<string>();

  const [errorTitle, setErrorTitle] = useState<string>();

  const [schemaError, setSchemaError] = useState<null | string>(null);

  const [availableSchemas, setAvailableSchemas] = useState<string[]>([]);

  const [selectedSchemas, setSelectedSchemas] = useState<string[]>(
    dataSource?.credentials?.selected_schemas || []
  );

  const [snowflakeKey, setSnowflakeKey] = useState<SnowflakeKeyPair | null>(
    dataSource?.credentials?.snowflake_public_key
      ? {
          publicKey: dataSource?.credentials?.snowflake_public_key,
        }
      : null
  );

  const [snowflakeKeypairMode, setSnowflakeKeypairMode] = useState<SnowflakeKeyPairAction>();

  const dataSourceId = dataSource?.data_source_id;

  // MotherDuck's only advanced setting is the schema picker, which needs a live
  // connection to list schemas, so it's only useful once the connection exists.
  // Other connectors keep pre-connection advanced settings (SSH tunnel, TLS).
  const advancedSettingsAvailable =
    dataSourceType !== "mongodb" && (dataSourceType !== "motherduck" || Boolean(dataSourceId));

  const [inputError, setInputError] = useState<
    {
      host?: string;
      port?: string;
      username?: string;
      password?: string;
    } & SSHTunnelFormError
  >({});

  const [sshTunnelConfig, setSSHTunnelConfig] = useState<TunnelConfig>(() =>
    extractTunnelConfig(dataSource)
  );

  const sshTunnelSupported = isTunnelEnabled(dataSourceType);

  const dataSourceName = DataSourceTypeDisplayNames[dataSourceType];

  const shouldUseSecondaryForm: boolean =
    dataSource?.credentials?.host?.includes("mongodb+srv") ||
    !!useSecondaryForm ||
    dataSourceType === "databricks";

  const connectionStringPrefix =
    dataSourceType === "databricks" ? "jdbc:databricks" : "mongodb+srv";

  const connectionFieldName = dataSourceType === "databricks" ? "JDBC URL" : "Connection String";

  useEffect(() => {
    if (!dataSourceId) {
      track("conn_create_viewed", {
        event_from: fromConnectedSource
          ? "add_connection"
          : fromOnboarding
          ? "app_onboarding"
          : "add_import",
        import_data_source: dataSourceType,
        data_source_id: dataSourceId,
      });
    }
  }, []);

  useEffect(() => {
    if (dataSourceId) {
      reloadSchema();
    }
  }, [dataSourceId]);

  const onSubmit = async () => {
    if (!isValidateInputs()) {
      return;
    }

    // MotherDuck is PAT-only cloud access with no IP allowlisting, so skip the
    // firewall/whitelist footer step and validate the token straight away.
    if (!dataSourceId && !ipInfoFooterShown && dataSourceType !== "motherduck") {
      setIpInfoFooterShown(true);
      setShowWhiteListInfoFooter(true);
      return;
    }

    const use_ssh_tunnel = sshTunnelSupported && sshTunnelConfig.use_tunnel;

    track(dataSourceId ? "conn_edit_started" : "conn_create_started", {
      import_data_source: dataSourceType,
      conn_data_source: dataSourceType,
      edited_sharing: false,
      data_source_id: dataSourceId,
      use_ssh_tunnel,
    });

    setLoading(true);
    setConnectionError(null);
    setErrorTitle(undefined);
    setSourceError(undefined);

    try {
      const credentials: DataSourceCredentials = {
        ...(dataSourceType === "snowflake" &&
          enableSnowflakeKeyPairAuth && {
            snowflake_transient_encrypted_private_key: snowflakeKey?.privateKey,
            ...dataSource?.credentials,
          }),
        username,
        password,
        host,
        port,
        database_name: dbName,
        snowflake_warehouse_name: warehouse,
        selected_schemas: selectedSchemas,
        ...(dataSourceType === "arrow_flight_sql" && {
          disable_tls: !useTls,
          ...(customCallHeaders.filter(h => h.key).length > 0 && {
            arrow_flight_sql_call_headers: customCallHeaders.filter(h => h.key),
          }),
        }),
        ...(sshTunnelSupported ? sshTunnelConfig : {}),
      };

      const { data: checkResp } = await apiClient.checkConnection({
        data_source_type: dataSourceType,
        check_connection_request: { credentials },
      });

      if (checkResp.valid) {
        if (dataSourceType === "snowflake") {
          credentials.host = checkResp.payload?.snowflake_account;
          credentials.snowflake_warehouse_name = checkResp.payload?.snowflake_warehouse_name;
        }

        const dataSourceBody: CreateDataSourceRequest["data_source"] = {
          default_schema_name: checkResp?.payload?.default_schema_name ?? undefined,
          ...dataSource,
          data_source_type: dataSourceType,
          data_source_name: dataSource?.data_source_name || dataSourceName,
          credentials,
        } as DataSource;

        const { data: upsertDataSourceResponse } = await (dataSourceId
          ? apiClient.updateDataSource({
              data_source_id: dataSourceId,
              update_data_source_request: {
                data_source: dataSourceBody,
              },
            })
          : apiClient.createDataSource({
              create_data_source_request: {
                data_source: dataSourceBody,
              },
            }));

        track(dataSourceId ? "conn_edit_done" : "conn_create_done", {
          import_data_source: dataSourceType,
          edited_sharing: false,
          data_source_id: dataSourceId,
          use_ssh_tunnel,
        });

        if (!disableMessages && dataSourceId) {
          message?.success("Connection saved");
        }

        if (dataSourceId) {
          onDataSourceUpserted(upsertDataSourceResponse.data_source);
        } else {
          initPostDataSourceCreation({
            dataSource: upsertDataSourceResponse.data_source,
            onComplete: dataSource => onDataSourceUpserted(dataSource),
          });
        }
      } else if (checkResp.error) {
        track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
          import_data_source: dataSourceType,
          data_source_id: dataSourceId,
          reason: checkResp.error,
        });

        setConnectionError(checkResp.error);

        if (checkResp.source_error) {
          setSourceError(checkResp.source_error);
        }

        if (checkResp.title) {
          setErrorTitle(checkResp.title);
        }
      } else if (checkResp.source_error) {
        track(dataSourceId ? "conn_edit_failed" : "conn_create_failed", {
          import_data_source: dataSourceType,
          data_source_id: dataSourceId,
          reason: checkResp.source_error,
        });

        setSourceError(checkResp.source_error);

        if (checkResp.title) {
          setErrorTitle(checkResp.title);
        }
      } else {
        sentryCapture?.({
          context: { response: checkResp },
          error: new Error("check_connection API returned valid=false but no structured error"),
          name: `AuthorizeSQL: ${
            dataSourceId ? "PUT" : "POST"
          } /api/data_source_apis/${dataSourceType}/check_connection`,
        });

        setConnectionError("Unknown error");
      }
    } catch (error) {
      sentryCapture?.({
        error,
        name: `AuthorizeSQL: ${
          dataSourceId ? "PUT" : "POST"
        } /api/data_source_apis/${dataSourceType}/check_connection`,
      });

      setConnectionError(
        getDataSourceApiErrorMessage?.(error) ||
          "Please check the connection information below and try again."
      );
    }

    setLoading(false);

    setShowWhiteListInfoFooter(false);
  };

  const reloadSchema = async () => {
    setSchemaLoading(true);

    try {
      if (dataSourceId) {
        const { data: response } = await apiClient.sqlGetSchema({
          data_source_type: dataSourceType,
          sql_get_schema_request: {
            data_source_id: dataSourceId,
            force_update_cache: true,
            show_all_available_schemas: true,
          },
        });

        if (response?.available_schemas) {
          const schemas = response?.available_schemas ?? [];
          setAvailableSchemas(schemas);
        }
      }

      setReloadWarning(false);

      setSchemaLoading(false);
    } catch (error) {
      sentryCapture?.({
        error,
        name: `AuthorizeSQL: POST /api/data_source_apis/${dataSourceType}/get_schema`,
      });

      setSchemaError(
        getDataSourceApiErrorMessage?.(error) ||
          "Oops, error occured while reloading schema. Please try again."
      );

      setSchemaLoading(false);
    }
  };

  const isValidateInputs = (): boolean => {
    if (shouldUseSecondaryForm) {
      return true;
    }

    if (dataSourceType === "motherduck") {
      if (!password) {
        setInputError({ ...inputError, password: "Please enter your Personal Access Token" });
        return false;
      }
      setInputError({});
      return true;
    }

    if (!/^[\w\.\-]*$/.test(host)) {
      setInputError({ ...inputError, host: "Please enter a valid host" });
      return false;
    }

    if (
      dataSourceType === "arrow_flight_sql"
        ? port !== undefined && isNaN(port)
        : port === undefined || isNaN(port)
    ) {
      setInputError({ ...inputError, host: "Please enter a valid port" });
      return false;
    }

    const SSHConfigError = validateSSHConfig(sshTunnelConfig);
    if (SSHConfigError) {
      setInputError({ ...inputError, ...SSHConfigError });
      return false;
    }

    setInputError({});

    return true;
  };

  const getHelpInstructions = () => {
    switch (dataSourceType) {
      // Supabase surfaces its "Direct connection" string most prominently, but that host is
      // IPv6-only on lower tiers and the username differs from the pooler's, so following the
      // dashboard verbatim fails. Spell out the pooler values instead.
      case "supabase":
        return (
          <div>
            <p>
              <strong>To find your Supabase connection details:</strong>
            </p>

            <p>
              1. Open your project in{" "}
              <a href="https://supabase.com/dashboard" target="_blank">
                Supabase
              </a>
              , then go to <strong>Project Settings → Database → Connection string</strong>.
            </p>

            <p>
              2. Select the <strong>Session pooler</strong> tab. Do not use{" "}
              <strong>Direct connection</strong> — that host only accepts IPv6 unless your project
              has the IPv4 add-on, so it will time out here.
            </p>

            <p>
              3. <strong>Host</strong> looks like{" "}
              <code>aws-0-&lt;region&gt;.pooler.supabase.com</code> and <strong>Port</strong> is{" "}
              <code>5432</code>.
            </p>

            <p>
              4. <strong>Username</strong> is <code>postgres.&lt;your-project-ref&gt;</code>. The
              project ref suffix is required — plain <code>postgres</code> will be rejected by the
              pooler.
            </p>

            <p>
              5. <strong>Database Name</strong> is <code>postgres</code>, and{" "}
              <strong>Password</strong> is your database password (Project Settings → Database →
              Reset database password, if you don&apos;t have it).
            </p>

            <p>
              <a
                href="https://supabase.com/docs/guides/database/connecting-to-postgres"
                target="_blank"
              >
                See Supabase&apos;s connection guide.
              </a>
            </p>
          </div>
        );

      case "motherduck":
        return (
          <div>
            <p>
              <strong>To get a MotherDuck Personal Access Token:</strong>
            </p>

            <p>
              1. Log in to the{" "}
              <a href="https://app.motherduck.com" target="_blank">
                MotherDuck UI
              </a>
              .
            </p>

            <p>2. Click your organization name in the top left, then select Settings.</p>

            <p>
              3. Click <strong>+ Create token</strong>, enter a name, and choose the token type.
            </p>

            <p>4. Click Create token, then copy it and paste it here.</p>

            <p>
              You can paste an existing token instead.{" "}
              <a
                href="https://motherduck.com/docs/key-tasks/authenticating-and-connecting-to-motherduck/authenticating-to-motherduck/"
                target="_blank"
              >
                See the full guide.
              </a>
            </p>
          </div>
        );

      case "mongodb":
        return (
          <div>
            <p>
              <strong>To get your MongoDB connection string on Atlas:</strong>
            </p>

            <p>
              1. Login to MongoDB Atlas by{" "}
              <a href="https://cloud.mongodb.com" target="_blank">
                clicking here.
              </a>
            </p>

            <p>
              2. Once logged in, head to the Database view by clicking on 'Database' from the left
              pane.
            </p>

            <p>3. Click on 'Connect' against the database deployment.</p>

            <p>
              4. From the popup that loads, click on Compass and you will find your connection
              string listed in the following format
            </p>

            <p>
              <strong>mongodb+srv:&lt;username&gt;:&lt;password&gt;@&lt;mongo_url&gt;</strong>
            </p>
          </div>
        );

      default:
        return (
          <div>
            <br />

            <p>
              You'll need a JDBC URL along with a Personal Access Token (PAT) to connect to your
              Databricks instance.
              <br /> <br /> To obtain the URL and PAT, follow the guidelines as demonstrated in
              <a href="https://www.youtube.com/watch?v=rCSY7hUwhuI" target="_blank">
                {" "}
                this video.
              </a>
            </p>

            <p>
              If you prefer to follow written guidelines, you may follow the steps
              <a
                href="https://help.coefficient.io/hc/en-us/articles/27594448244251-Databricks#h_01J3D5QMJMG64NFN4F8K99TH7C"
                target="_blank"
              >
                {" "}
                here.
              </a>
            </p>
          </div>
        );
    }
  };

  const passwordFieldEnabled = dataSourceType === "snowflake" ? !enableSnowflakeKeyPairAuth : true;

  return (
    <>
      {showWhiteListInfoFooter && (
        <DisabledOverlay className="dark" style={{ zIndex: 200, position: "fixed" }} />
      )}

      <StyledContent $hidePageHeader={!!isPPressComponent}>
        {!dataSourceId && (!shouldUseSecondaryForm || dataSourceType === "databricks") && (
          <Graphics
            graphics={
              <div className="graphicsLogo">
                <ConnectPlugIcon />
                {LOGOS_BY_DATA_SOURCE_TYPE[dataSourceType]}
              </div>
            }
            title={`Let's connect to ${dataSourceName}`}
            description={customDescription}
            descriptionStyle={
              dataSourceType === "snowflake"
                ? {
                    maxWidth: "100%",
                    padding: "0 10px",
                  }
                : undefined
            }
            descriptionHtml={""}
          />
        )}

        {dataSourceId &&
          renderDataSourceItem?.({
            dataSource,
            setAvailableSchemas,
          })}

        {dataSourceId &&
          enableSnowflakeKeyPairAuth &&
          dataSourceType === "snowflake" &&
          !snowflakeKey && (
            <ErrorMessage
              screen="authorizeSQL"
              variant="warning"
              title={"Deprecation of passwords"}
              message="Snowflake is phasing out password-based authentication. Edit connection to configure a key-pair and keep your imports running."
              dataSourceType={dataSourceType}
            />
          )}

        {(connectionError !== null || sourceError) &&
          (sourceError && sourceError.includes("not valid") ? (
            <ErrorMessage
              screen="authorizeSQL"
              title={errorTitle ? errorTitle : "Unable to connect to database"}
              messageHtml={connectionError ? connectionError : undefined}
              secondaryMessage={sourceError}
              dataSourceType={dataSourceType}
            />
          ) : (
            <ErrorMessage
              screen="authorizeSQL"
              title={
                errorTitle
                  ? errorTitle
                  : connectionError?.includes(" SSH")
                  ? "SSH Connection Error"
                  : "Unable to connect to database"
              }
              message={connectionError ? connectionError : undefined}
              secondaryMessage={sourceError}
              dataSourceType={dataSourceType}
            />
          ))}

        {connectionError && renderConnectionHelpCard?.({ dataSourceType: dataSourceType ?? "" })}

        {!shouldUseSecondaryForm && (
          <>
            {dataSourceType !== "snowflake" && dataSourceType !== "motherduck" && (
              <>
                <FormField>
                  Host
                  <Input
                    size="middle"
                    onChange={e => setHost(e.currentTarget.value)}
                    onBlur={e => setHost(host.replace(/(^\w+:|^)\/\//, ""))}
                    value={host}
                    error={!!inputError.host}
                    placeholder={"db.example.com"}
                  />
                  <StyledInstructionText className="simple-text">
                    {"Enter the IP address or hostname (localhost is not valid) and make sure to "}
                    <div
                      className="highlighted-text"
                      onClick={() => setShowWhitelistInfoPopup(true)}
                    >
                      whitelist our IP's
                    </div>
                  </StyledInstructionText>
                  {dataSourceType === "supabase" && (
                    <StyledInstructionText className="simple-text">
                      <span
                        className="styledEdit"
                        onClick={() => setShowHelpModal(true)}
                        style={{ color: "blue", cursor: "pointer" }}
                      >
                        {"Click here "}
                      </span>
                      for the Supabase host and username format — the Direct connection string shown
                      in Supabase will not work.
                    </StyledInstructionText>
                  )}
                  {inputError.host && <ErrorText>{inputError.host}</ErrorText>}
                </FormField>
                <FormField>
                  Port
                  <Input
                    size="middle"
                    type="number"
                    onChange={e => setPort(Number(e.currentTarget.value))}
                    value={port}
                    error={!!inputError.port}
                  />
                  {inputError.port && <ErrorText>{inputError.port}</ErrorText>}
                </FormField>
                <FormField>
                  Database Name
                  <Input
                    size="middle"
                    onChange={e => setDbName(e.currentTarget.value)}
                    value={dbName}
                    placeholder={"example_prod"}
                  />
                </FormField>
              </>
            )}

            {dataSourceType !== "motherduck" && (
              <FormField>
                Username
                <Input
                  size="middle"
                  onChange={e => setUsername(e.currentTarget.value)}
                  value={username}
                />
              </FormField>
            )}

            {passwordFieldEnabled && (
              <FormField style={dataSourceType === "motherduck" ? { marginBottom: 28 } : undefined}>
                {dataSourceType === "motherduck" ? "Personal Access Token" : "Password"}
                <Input
                  type="password"
                  size="middle"
                  onChange={e => setPassword(e.currentTarget.value)}
                  value={password}
                  error={!!inputError.password}
                />
                {dataSourceType === "motherduck" && (
                  <StyledInstructionText className="simple-text">
                    <span
                      className="styledEdit"
                      onClick={() => setShowHelpModal(true)}
                      style={{ color: "blue", cursor: "pointer" }}
                    >
                      {"Click here "}
                    </span>
                    to learn how to get a Personal Access Token.
                  </StyledInstructionText>
                )}
                {inputError.password && <ErrorText>{inputError.password}</ErrorText>}
              </FormField>
            )}

            {!passwordFieldEnabled && (
              <FormField>
                Private Key
                {snowflakeKey?.publicKey ? (
                  <>
                    <Input
                      bordered={false}
                      value={snowflakeKey?.publicKey.toString() || ""}
                      disabled
                      type="password"
                      suffix={
                        <Typography
                          color="textPrimary"
                          style={{
                            color: COLORS.coeblue4,
                            cursor: "pointer",
                            position: "relative",
                            left: "8px",
                          }}
                          onClick={() => setSnowflakeKeypairMode("generate")}
                        >
                          edit
                        </Typography>
                      }
                    />
                    <StyledInstructionText className="simple-text">
                      <div
                        className="highlighted-text"
                        onClick={() => setSnowflakeKeypairMode("view")}
                      >
                        Show instructions
                      </div>
                    </StyledInstructionText>
                  </>
                ) : (
                  <>
                    <Button
                      type="default"
                      style={{ width: "100%", marginTop: 2 }}
                      onClick={() => setSnowflakeKeypairMode("generate")}
                    >
                      Configure
                    </Button>
                    {dataSourceId && (
                      <StyledInstructionText className="simple-text">
                        Alternatively, you could keep the password based auth active temporarily by
                        disabling MFA.{" "}
                        <span
                          className="highlighted-text"
                          onClick={() => setSnowflakeKeypairMode("disable")}
                        >
                          Learn more
                        </span>
                      </StyledInstructionText>
                    )}
                  </>
                )}
              </FormField>
            )}

            {dataSourceType === "snowflake" && (
              <>
                <FormField>
                  <FlexRow justifyContent="space-between">
                    Database Name
                    <Typography
                      fontSize={12}
                      lineHeight={"22px"}
                      color="textLink"
                      fontWeight="bold"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowDatabaseNameModal(true)}
                    >
                      How to find
                    </Typography>
                  </FlexRow>
                  <Input
                    size="middle"
                    onChange={e => setDbName(e.currentTarget.value)}
                    value={dbName}
                    placeholder={"ABC12345"}
                  />
                </FormField>
                <FormField>
                  <FlexRow justifyContent="space-between">
                    Account Identifier
                    <Typography
                      fontSize={12}
                      lineHeight={"22px"}
                      color="textLink"
                      fontWeight="bold"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowAccountIdentifierModal(true)}
                    >
                      How to find
                    </Typography>
                  </FlexRow>
                  <Input
                    size="middle"
                    onChange={e => setHost(e.currentTarget.value)}
                    onPaste={e => {
                      const pastedText = e.clipboardData.getData("text");
                      // Parse Snowflake URL format: https://app.snowflake.com/<org>/<account_identifier>/#/...
                      const snowflakeUrlMatch = pastedText.match(
                        /app\.snowflake\.com\/([^\/]+)\/([^\/\#]+)/
                      );
                      const [host, org, accountIdentifier] = snowflakeUrlMatch || [];
                      if (accountIdentifier?.trim()) {
                        e.preventDefault();
                        setHost(accountIdentifier.trim());
                      }
                    }}
                    onBlur={e => setHost(host.replace(/(^\w+:|^)\/\//, ""))}
                    value={host}
                    error={!!inputError.host}
                    placeholder="MYACCOUNT"
                  />

                  {inputError.host && <ErrorText>{inputError.host}</ErrorText>}
                </FormField>

                <FormField>
                  <FlexRow>
                    Warehouse{" "}
                    <Tooltip title="Leave blank to use default warehouse" placement="topRight">
                      <TiltedInfoCircle height="14px" width="14px" />
                    </Tooltip>
                  </FlexRow>

                  <Input
                    size="middle"
                    error={isWarehouseConnectionError(connectionError)}
                    onChange={e => setWarehouse(e.currentTarget.value)}
                    value={warehouse}
                    placeholder={"MY_WAREHOUSE"}
                  />
                  {isWarehouseConnectionError(connectionError) && (
                    <div style={{ color: "red" }}>{connectionError}</div>
                  )}
                </FormField>
              </>
            )}
          </>
        )}

        {shouldUseSecondaryForm && (
          <>
            <FormField>
              {connectionFieldName}

              <Input
                size="middle"
                onChange={e => setHost(e.currentTarget.value)}
                onBlur={e => setHost(host.replace(/(^\w+:|^)\/\//, ""))}
                value={dataSourceType === "databricks" ? host : getMaskedConnectionString(host)}
                error={!!inputError.host}
                placeholder={
                  dataSourceType === "mongodb"
                    ? "mongodb+srv:<user>:<password>@<url>"
                    : dataSourceType === "databricks"
                    ? "jdbc:databricks://<url>"
                    : ""
                }
              />

              <StyledInstructionText className="simple-text">
                {"A valid " +
                  connectionFieldName.toLowerCase() +
                  " starts with " +
                  connectionStringPrefix +
                  " . "}
                <div>
                  <span
                    className="styledEdit"
                    onClick={() => setShowHelpModal(true)}
                    style={{ color: "blue" }}
                  >
                    {"Click here "}
                  </span>
                  {dataSourceType === "mongodb"
                    ? "to locate your connection string on MongoDB Atlas."
                    : "to locate your JDBC URL on Databricks."}
                </div>
              </StyledInstructionText>

              {inputError.host && <ErrorText>{inputError.host}</ErrorText>}
            </FormField>

            <FormField>
              {dataSourceType === "databricks" ? "Catalog Name" : "Database Name"}
              <Input
                size="middle"
                onChange={e => setDbName(e.currentTarget.value)}
                value={dbName}
                placeholder={"example_prod"}
              />
            </FormField>
          </>
        )}

        {advancedSettingsAvailable && (
          <FormField style={{ marginBottom: 8 }}>
            <Button
              noPadding
              type="link"
              style={{ color: COLORS.black85, fontWeight: "normal" }}
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            >
              <FlexRow>
                <span>Advanced settings</span>

                <ChevronDown
                  fill="inhert"
                  style={showAdvancedSettings ? { transform: "rotate(180deg)", marginTop: 2 } : {}}
                />
              </FlexRow>
            </Button>
          </FormField>
        )}

        {advancedSettingsAvailable && showAdvancedSettings && (
          <>
            {dataSourceType === "arrow_flight_sql" && (
              <FormField style={{ marginTop: 8 }}>
                <span>Custom Call Headers</span>
                <KeyValueListInput
                  items={customCallHeaders}
                  onChange={setCustomCallHeaders}
                  addButton={
                    <Button
                      type="link"
                      size="small"
                      onClick={() =>
                        setCustomCallHeaders([...customCallHeaders, { key: "", value: "" }])
                      }
                    >
                      + Add header
                    </Button>
                  }
                />
              </FormField>
            )}

            <FormField style={{ marginTop: 8 }}>
              <LimitSchemasInput
                disabled={false}
                dataSourceId={dataSourceId}
                availableSchemas={availableSchemas}
                selectedSchemas={selectedSchemas}
                setSelectedSchemas={setSelectedSchemas}
                schemaLoading={schemaLoading}
                schemaError={schemaError}
                reloadSchema={reloadSchema}
              />
            </FormField>

            {sshTunnelSupported && (
              <SSHTunnelForm
                value={sshTunnelConfig}
                onChange={setSSHTunnelConfig}
                error={inputError}
              />
            )}

            {dataSourceType === "arrow_flight_sql" && (
              <FormField style={{ marginTop: 8 }}>
                <Checkbox checked={useTls} onChange={e => setUseTls(e.target.checked)}>
                  Connect using TLS encryption
                </Checkbox>
              </FormField>
            )}
          </>
        )}

        {shouldUseSecondaryForm && (
          <HelpButton onClick={() => setShowWhitelistInfoPopup(true)}>
            <QuestionCircleOutlined /> Whitelist our IPs
          </HelpButton>
        )}

        {!connectionError && !dataSourceId && renderConnectionHelpCard && (
          <div style={{ marginTop: showAdvancedSettings ? 22 : 0 }}>
            {renderConnectionHelpCard({ dataSourceType: dataSourceType ?? "" })}
          </div>
        )}
      </StyledContent>

      <StyledFooter>
        {showWhiteListInfoFooter && (
          <>
            <StyledCardHeader title="Is your database behind a firewall or private network?">
              <StyledButton className="close" onClick={() => setShowWhiteListInfoFooter(false)}>
                x
              </StyledButton>
            </StyledCardHeader>

            <IPWhiteListInfoCard dataSourceType={dataSourceType ?? ""} />
          </>
        )}

        <ButtonSection className={showWhiteListInfoFooter ? "ip-info-footer" : ""}>
          {dataSourceId && (
            <Button type="default" onClick={() => onCancel()}>
              Cancel
            </Button>
          )}

          <Button
            disabled={
              loading ||
              // eslint-disable-next-line @coeff/strict-boolean-expressions
              (dataSourceType === "motherduck"
                ? !password
                : !(
                    host &&
                    (dbName || dataSourceType === "arrow_flight_sql") &&
                    username &&
                    (dataSourceType === "snowflake" && enableSnowflakeKeyPairAuth
                      ? !!snowflakeKey?.publicKey
                      : password) &&
                    port !== 0
                  ) &&
                  !(
                    host &&
                    (dbName || dataSourceType === "arrow_flight_sql") &&
                    shouldUseSecondaryForm
                  ))
            }
            target="_blank"
            type="primary"
            onClick={() => onSubmit()}
          >
            {loading ? <DotsLoader /> : dataSourceId ? "Save" : "Connect"}
          </Button>
        </ButtonSection>

        {!dataSourceId && <SecurityInfo />}
      </StyledFooter>

      <ConfirmBox
        confirmLoading={schemaLoading}
        cancelText={schemaError ? "Dismiss" : "Cancel"}
        okText={schemaError ? "Try again" : "Reload"}
        title={`${schemaLoading ? "Reloading" : "Reload"} Schema`}
        visible={reloadWarning}
        onCancel={() => {
          setReloadWarning(false);
          setSchemaLoading(false);
          setSchemaError(null);
        }}
        onOk={reloadSchema}
      >
        {schemaError ? (
          <ErrorMessage
            screen="authorizeSQL"
            dataSourceType={dataSourceType}
            title={schemaError}
            style={{ margin: "5px -10px 0px" }}
          />
        ) : schemaLoading ? (
          ""
        ) : (
          "Refresh table and column names within this database"
        )}
      </ConfirmBox>

      {snowflakeKeypairMode && (
        <SnowflakeKeyPairModel
          userName={username}
          keyPair={snowflakeKey}
          action={snowflakeKeypairMode}
          onClose={keypair => {
            setSnowflakeKey(keypair);
            setSnowflakeKeypairMode(undefined);
          }}
        />
      )}

      {showWhitelistInfoPopup && (
        <IPWhitelistInfoPopup
          dataSourceType={dataSourceType ?? ""}
          setShowIPWhiteListInfoPopup={setShowWhitelistInfoPopup}
        />
      )}

      <Modal visible={showHelpModal} footer={null} onCancel={() => setShowHelpModal(false)}>
        {getHelpInstructions()}
      </Modal>

      <StyledCoeffModal
        visible={showAccountIdentifierModal}
        footer={
          <FlexRow justifyContent="flex-end">
            <Button type="link" onClick={() => setShowAccountIdentifierModal(false)}>
              Got it
            </Button>
          </FlexRow>
        }
        onCancel={() => setShowAccountIdentifierModal(false)}
        centered
      >
        <FlexColumn>
          <Typography fontSize={18} fontWeight={500} lineHeight={"20px"} color="textPrimary">
            Account Identifier
          </Typography>
          <Typography fontSize={14} lineHeight={"22px"} color="textSecondary">
            Find your account identifier in the lower left-hand side of your Snowflake account.
          </Typography>

          <div>
            <Button
              type="link"
              noPadding
              onClick={() =>
                window.open(
                  "https://docs.snowflake.com/en/user-guide/admin-account-identifier",
                  "_blank"
                )
              }
            >
              View Snowflake help
            </Button>
          </div>

          <ModalImage
            src={`${AssetsHost()}/snowflake-account-identifier.png`}
            alt="Snowflake Account Identifier"
            minHeight={170}
          />
        </FlexColumn>
      </StyledCoeffModal>

      <StyledCoeffModal
        visible={showDatabaseNameModal}
        onCancel={() => setShowDatabaseNameModal(false)}
        footer={
          <FlexRow justifyContent="flex-end">
            <Button type="link" onClick={() => setShowDatabaseNameModal(false)}>
              Got it
            </Button>
          </FlexRow>
        }
        centered
      >
        <FlexColumn>
          <Typography fontSize={18} fontWeight={500} lineHeight={"20px"} color="textPrimary">
            Database name
          </Typography>

          <Typography fontSize={14} lineHeight={"22px"} color="textSecondary">
            When logged into Snowflake, you can find a list of your database names under{" "}
            <strong>Data &gt; Databases</strong>
          </Typography>

          <ModalImage
            src={`${AssetsHost()}/snowflake-database-name.png`}
            alt="Snowflake Database Name"
            minHeight={230}
          />
        </FlexColumn>
      </StyledCoeffModal>
    </>
  );
};

export const AuthorizeSQLContent = AuthorizeSQLContentBase;
